<script setup lang="ts">
/**
 * Shadow panel of a bounded text. Everything that is not specific to a text lives in
 *  useShadowSettings + ShadowSettings: this only says which element to write into, and when a text
 *  can't cast a shadow.
 */
import type { BoundedTextElement } from '@/typings/customilyElements/canvasElements';
import type { CanvasEx } from '@/typings/canvas';
import ShadowSettings from '@/components/DesignStudio/ShadowSettings.vue';
import { useShadowSettings } from '@/composables/shadowSettings';

const props = withDefaults(
    defineProps<{
        canvas: CanvasEx;
        textElement?: BoundedTextElement;
        editMode?: boolean;
        cmykMode?: boolean;
        isPreview?: boolean;
        exportFormat?: string;
    }>(),
    {
        textElement: undefined,
        isPreview: false
    }
);

const emit = defineEmits<{
    (e: 'shadow-change'): void;
    (e: 'open-shadow-color-library-selector'): void;
}>();

const shadow = useShadowSettings<BoundedTextElement>({
    element: () => props.textElement,
    canvas: () => props.canvas,
    editMode: () => props.editMode,
    exportFormat: () => props.exportFormat,
    // A textured text paints the texture over the glyphs, there is no silhouette left to cast
    supportsShadow: (element) => element.type === 'bounded-text' && !element.texture,
    openLibrarySelector: () => emit('open-shadow-color-library-selector'),
    onShadowChange: () => emit('shadow-change')
});
</script>

<template>
    <shadow-settings :shadow="shadow" :cmyk-mode="props.cmykMode" :is-preview="props.isPreview" variant="text" />
</template>
